import React from "react";
import { ArrowRight, Brain, Clock, HeartHandshake, Compass, Mail, MessageCircle } from "lucide-react";

import ROUTES_CONFIG from "@/routes.json";

// Real URL for a page id, in the given language (keeps internal links crawlable)
export const hrefForPage = (page, lang = 'ro') => {
  const entry = ROUTES_CONFIG.pages.find((p) => p.page === page);
  return entry ? entry[lang] || entry.ro : '#';
};

export const CALENDLY_BOOKING_URL = "https://calendly.com/ruxandrasterbet/therapy-session?month=2026-09";
export const WHATSAPP_URL = "https://wa.me/qr/222ZB3WCDT47H1";
export const CONTACT_EMAIL = "ruxandrasterbet@gmail.com";

export const servicesData = [
  {
    id: 'service-psihoterapie-adulti',
    icon: Brain,
    accent: { text: 'text-sage', bg: 'bg-sage/12', hover: 'group-hover:bg-sage/20' },
    theme: { card: '#EAF1E8', accent: '#8FA793', heading: '#314239', border: '#D8E3D5' },
    titleKey: 'svc1Title',
    metaKey: 'svc1Meta',
    descKey: 'svc1Desc',
    ctaKey: 'svc1Cta',
  },
  {
    id: 'service-consiliere-copii',
    icon: HeartHandshake,
    accent: { text: 'text-clay', bg: 'bg-clay/12', hover: 'group-hover:bg-clay/20' },
    theme: { card: '#F7E8E0', accent: '#D99679', heading: '#4A3A34', border: '#EED9CE' },
    titleKey: 'svc2Title',
    metaKey: 'svc2Meta',
    descKey: 'svc2Desc',
    ctaKey: 'svc2Cta',
  },
  {
    id: 'service-consiliere-vocationala',
    icon: Compass,
    accent: { text: 'text-[#8FA3B2]', bg: 'bg-[#A4B4C1]/15', hover: 'group-hover:bg-[#A4B4C1]/25' },
    theme: { card: '#F6EEDC', accent: '#C9A663', heading: '#4B4437', border: '#EADFC5' },
    titleKey: 'svc3Title',
    metaKey: 'svc3Meta',
    descKey: 'svc3Desc',
    ctaKey: 'svc3Cta',
  },
];

export const findService = (id) => {
  for (const service of servicesData) {
    if (service.id === id) return service;
    const category = (service.categories || []).find((c) => c.id === id);
    if (category) return { ...service, ...category, parentId: service.id };
  }
  return null;
};

// Question / contact block reused across the services area and detail pages
export const QuestionBlock = ({ t }) => (
  <div
    data-testid="services-question-block"
    className="bg-white rounded-2xl border border-stone-100 shadow-sm p-8 md:p-10"
  >
    <h3 className="font-serif text-2xl text-sage-900 mb-3">{t.questionHeading}</h3>
    <p className="text-sage-900/70 leading-relaxed mb-7 max-w-2xl">{t.questionText}</p>
    <div className="flex flex-col sm:flex-row gap-3">
      <a
        data-testid="services-whatsapp-link"
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-white px-6 py-3 rounded-full text-sm font-medium transition-all duration-300"
      >
        <MessageCircle size={16} strokeWidth={1.75} />
        {t.questionWhatsapp}
      </a>
      <a
        data-testid="services-email-link"
        href={`mailto:${CONTACT_EMAIL}`}
        className="inline-flex items-center justify-center gap-2 border border-sage/30 hover:border-sage hover:bg-sage/5 text-sage-900 px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 break-all"
      >
        <Mail size={16} strokeWidth={1.75} />
        {CONTACT_EMAIL}
      </a>
    </div>
  </div>
);

export const ServiceCard = ({ service, index, isVisible, t, learnMoreHref = '#', onLearnMore, onBook }) => {
  const theme = service.theme || { card: '#FFFFFF', accent: '#8FA793', heading: '#314239', border: '#E7E5E4' };
  return (
  <div
    data-testid={`service-card-${service.id}`}
    className={`p-8 md:p-10 rounded-2xl border md:hover:shadow-xl transition-all duration-500 flex flex-col group md:hover:-translate-y-[3px] ${
      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
    }`}
    style={{
      transitionDelay: `${150 + index * 130}ms`,
      backgroundColor: theme.card,
      borderColor: theme.border,
    }}
  >
    <div
      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 md:group-hover:scale-105"
      style={{ backgroundColor: `${theme.accent}26` }}
    >
      <service.icon style={{ color: theme.accent }} size={26} strokeWidth={1.5} />
    </div>

    <h3 className="font-serif text-[1.7rem] mb-3 leading-snug" style={{ color: theme.heading }}>
      <a
        data-testid={`service-title-link-${service.id}`}
        href={learnMoreHref}
        onClick={(e) => { e.preventDefault(); onLearnMore(service.id); }}
        className="hover:opacity-70 transition-opacity"
      >
        {t[service.titleKey]}
      </a>
    </h3>

    {service.metaKey && (
      <p className="inline-flex items-center gap-2 text-[15px] text-sage-900/70 mb-5">
        <Clock size={15} strokeWidth={1.5} style={{ color: theme.accent }} />
        {t[service.metaKey]}
      </p>
    )}

    <p className="text-sage-900/70 text-[17px] leading-[1.8] mb-8 flex-grow">{t[service.descKey]}</p>

    <div className="flex flex-col sm:flex-row gap-3">
      <a
        data-testid={`service-learn-more-${service.id}`}
        href={learnMoreHref}
        onClick={(e) => { e.preventDefault(); onLearnMore(service.id); }}
        className="group/link inline-flex items-center justify-center gap-2 bg-white/80 hover:bg-white border border-[#B8C0B8] hover:border-sage text-sage-900 px-7 py-3.5 rounded-full text-[15px] font-medium transition-all duration-200"
      >
        {t.serviceLearnMore}
        <ArrowRight size={16} className="transition-transform duration-200 group-hover/link:translate-x-1" />
      </a>
      <a
        data-testid={`service-book-${service.id}`}
        href={CALENDLY_BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 bg-[#AA6244] hover:bg-[#8F5236] text-white px-7 py-3.5 rounded-full text-[15px] font-medium transition-all duration-300 shadow-md hover:shadow-lg"
      >
        {t[service.ctaKey]}
      </a>
    </div>
  </div>
  );
};
